import { createIcons, icons } from 'lucide';

// Initialize UI Elements
const inputArea = document.getElementById('input-area');
const outputWrapper = document.getElementById('output-wrapper');
const placeholderState = document.getElementById('placeholder-state');
const errorToast = document.getElementById('error-toast');
const errorMsg = document.getElementById('error-msg');
const statsLabel = document.getElementById('stats');

// Initialize Icons
createIcons({ icons });

// --- Core Logic ---

function isUrl(string) {
  try {
    new URL(string);
    return true;
  } catch (_) {
    return false;
  }
}

function escapeHtml(text) {
  if (text === undefined || text === null) return '';
  return text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function jsonToHtmlTable(data) {
  if (data === null) return '<span class="val-null">null</span>';
  if (data === undefined) return '<span class="val-null">undefined</span>';

  if (Array.isArray(data)) {
    if (data.length === 0) return '<span class="text-slate-400 text-xs">[]</span>';

    if (data.every(item => typeof item !== 'object' || item === null)) {
      return `<div class="flex flex-wrap gap-1">${data.map(item => jsonToHtmlTable(item)).join('<span class="text-slate-300">, </span>')}</div>`;
    }

    const keys = new Set();
    data.forEach(item => {
      if (typeof item === 'object' && item !== null) {
        Object.keys(item).forEach(k => keys.add(k));
      }
    });
    const allKeys = Array.from(keys);

    if (allKeys.length === 0) {
      return '<span class="text-slate-400 text-xs">[Complex Array]</span>';
    }

    let html = '<div class="overflow-x-auto"><table class="json-table"><thead><tr>';
    allKeys.forEach(key => {
      html += `<th>${escapeHtml(key)}</th>`;
    });
    html += '</tr></thead><tbody>';

    data.forEach(item => {
      html += '<tr>';
      if (typeof item === 'object' && item !== null) {
        allKeys.forEach(key => {
          html += `<td>${jsonToHtmlTable(item[key])}</td>`;
        });
      } else {
        html += `<td colspan="${allKeys.length}">${jsonToHtmlTable(item)}</td>`;
      }
      html += '</tr>';
    });
    html += '</tbody></table></div>';
    return html;

  } else if (typeof data === 'object') {
    const keys = Object.keys(data);
    if (keys.length === 0) return '<span class="text-slate-400 text-xs">{}</span>';

    let html = '<div class="nested-table-container"><table class="json-table">';
    keys.forEach(key => {
      html += `<tr><th style="width: 10px; white-space: nowrap;">${escapeHtml(key)}</th><td>${jsonToHtmlTable(data[key])}</td></tr>`;
    });
    html += '</table></div>';
    return html;
  } else {
    const type = typeof data;
    let content = escapeHtml(String(data));

    if (type === 'boolean') return `<span class="val-boolean">${content}</span>`;
    if (type === 'number') return `<span class="val-number">${content}</span>`;
    if (type === 'string') {
      if (isUrl(data)) {
        return `<a href="${data}" target="_blank" class="val-link">${content}</a>`;
      }
      return `<span class="val-string">${content}</span>`;
    }
    return content;
  }
}

function processJSON(raw) {
  if (!raw.trim()) {
    showPlaceholder();
    hideError();
    return;
  }

  try {
    const json = JSON.parse(raw);
    const html = jsonToHtmlTable(json);

    outputWrapper.innerHTML = html;
    showOutput();
    hideError();

    const size = new Blob([raw]).size;
    const keys = Array.isArray(json) ? json.length + " items" : Object.keys(json).length + " keys";
    statsLabel.innerText = `${keys} • ${formatBytes(size)}`;

  } catch (e) {
    showError(e.message);
  }
}

// --- Utilities ---

function formatBytes(bytes, decimals = 2) {
  if (!+bytes) return '0 Bytes';
  const k = 1024;
  const dm = decimals < 0 ? 0 : decimals;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
}

function debounce(func, wait) {
  let timeout;
  return function (...args) {
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(this, args), wait);
  };
}

function showError(msg) {
  errorToast.classList.remove('hidden', 'translate-y-10', 'opacity-0');
  errorMsg.innerText = msg;
}

function hideError() {
  errorToast.classList.add('translate-y-10', 'opacity-0');
  setTimeout(() => errorToast.classList.add('hidden'), 300);
}

function showOutput() {
  outputWrapper.classList.remove('hidden');
  placeholderState.classList.add('hidden');
}

function showPlaceholder() {
  outputWrapper.classList.add('hidden');
  placeholderState.classList.remove('hidden');
  statsLabel.innerText = "Waiting for input...";
}

// --- Event Listeners (Attached via JS, not HTML onclicks) ---

document.getElementById('btn-load-sample').addEventListener('click', () => {
  const sample = [
    {
      "id": 101,
      "name": "Alice Dev",
      "roles": ["Admin", "Editor"],
      "details": {
        "joined": "2023-01-15",
        "active": true,
        "metrics": { "logins": 45, "posts": 12 }
      }
    },
    {
      "id": 102,
      "name": "Bob Data",
      "roles": ["Viewer"],
      "details": {
        "joined": "2023-02-20",
        "active": false,
        "metrics": { "logins": 2, "posts": 0 }
      }
    }
  ];
  inputArea.value = JSON.stringify(sample, null, 2);
  processJSON(inputArea.value);
});

document.getElementById('btn-format').addEventListener('click', () => {
  try {
    const val = JSON.parse(inputArea.value);
    inputArea.value = JSON.stringify(val, null, 4);
    processJSON(inputArea.value);
  } catch (e) { showError("Cannot format invalid JSON"); }
});

document.getElementById('btn-minify').addEventListener('click', () => {
  try {
    const val = JSON.parse(inputArea.value);
    inputArea.value = JSON.stringify(val);
    processJSON(inputArea.value);
  } catch (e) { showError("Cannot minify invalid JSON"); }
});

document.getElementById('btn-clear').addEventListener('click', () => {
  inputArea.value = '';
  processJSON('');
  inputArea.focus();
});

document.getElementById('btn-copy-html').addEventListener('click', () => {
  if (outputWrapper.innerHTML === '') return;

  const styleBlock = `<style>table{border-collapse:collapse;width:100%;font-family:sans-serif;}th,td{border:1px solid #ddd;padding:8px;}th{background-color:#f2f2f2;text-align:left;}</style>`;
  const content = styleBlock + outputWrapper.innerHTML;

  navigator.clipboard.writeText(content).then(() => {
    const btn = document.getElementById('btn-copy-html');
    const originalText = btn.innerHTML;
    btn.innerHTML = `<i data-lucide="check" class="w-3 h-3"></i> Copied!`;
    createIcons({ icons });
    setTimeout(() => {
      btn.innerHTML = originalText;
      createIcons({ icons });
    }, 2000);
  });
});

document.getElementById('btn-copy-table').addEventListener('click', () => {
  if (outputWrapper.innerHTML === '') return;

  const range = document.createRange();
  range.selectNode(outputWrapper);
  window.getSelection().removeAllRanges();
  window.getSelection().addRange(range);

  try {
    document.execCommand('copy');
    window.getSelection().removeAllRanges();

    const btn = document.getElementById('btn-copy-table');
    const originalText = btn.innerHTML;
    btn.classList.remove('bg-blue-600', 'hover:bg-blue-700');
    btn.classList.add('bg-green-600', 'hover:bg-green-700');
    btn.innerHTML = `<i data-lucide="check" class="w-3 h-3"></i> Copied!`;
    createIcons({ icons });

    setTimeout(() => {
      btn.innerHTML = originalText;
      btn.classList.add('bg-blue-600', 'hover:bg-blue-700');
      btn.classList.remove('bg-green-600', 'hover:bg-green-700');
      createIcons({ icons });
    }, 2000);
  } catch (err) {
    showError("Failed to copy table");
  }
});

inputArea.addEventListener('input', debounce((e) => {
  processJSON(e.target.value);
}, 300));

window.onload = () => inputArea.focus();
